import type { AbstractConstructor, ExtensionDefinition, ExtensionInterface } from './extension-definition';

/**
 * Empty array instance to avoid creating new instances on every call.
 */
const EMPTY: unknown[] = [];

/**
 * Use a specific symbol instead of strings so that it is clear that this key is
 * ONLY intended for use inside this registry.
 */
export type ExtensionKey = symbol;

export type KeyedExtension<T> = { key: ExtensionKey; provider: T };

export class ExtensionRegistry {
  /**
   * For now this extensions list is only provided
   * for better debugging purposes to get a list of all extensions.
   */
  private extensions: ExtensionDefinition[] = [];

  /**
   * This array is just to check for duplicate keys. (As the symbols cannot be checked that easily.)
   */
  private extensionStringIds = new Set<string>();

  private providersByExtensionInterface = new Map<AbstractConstructor<ExtensionInterface>, ExtensionInterface[]>();
  private keyedProvidersByExtensionInterface = new Map<AbstractConstructor<ExtensionInterface>, KeyedExtension<unknown>[]>();
  private providerByKeyAndExtensionInterface = new Map<ExtensionKey, Map<AbstractConstructor<ExtensionInterface>, unknown>>();

  public registerExtension(extension: ExtensionDefinition) {
    if (this.extensionStringIds.has(extension.id)) {
      throw new Error(`Extension with id '${extension.id}' is already registered.`);
    }

    // Create a symbol to make clear that the "keys" returned by the registry are only intended for use by the registry.
    const key: ExtensionKey = Symbol(extension.id);

    this.extensions.push(extension);
    this.extensionStringIds.add(extension.id);

    for (const provider of extension.providers) {
      // Providers by extension interface
      if (!this.providersByExtensionInterface.has(provider.provide)) {
        this.providersByExtensionInterface.set(provider.provide, []);
      }
      this.providersByExtensionInterface.get(provider.provide)!.push(provider.useValue);

      // Keyed providers by extension interface
      if (!this.keyedProvidersByExtensionInterface.has(provider.provide)) {
        this.keyedProvidersByExtensionInterface.set(provider.provide, []);
      }
      this.keyedProvidersByExtensionInterface.get(provider.provide)!.push({
        key,
        provider: provider.useValue,
      });

      // Provider by id and extension interface
      if (!this.providerByKeyAndExtensionInterface.has(key)) {
        this.providerByKeyAndExtensionInterface.set(key, new Map());
      }
      this.providerByKeyAndExtensionInterface.get(key)!.set(provider.provide, provider.useValue);
    }
  }

  public getProviders<T extends ExtensionInterface>(extensionInterface: AbstractConstructor<T>): readonly T[] {
    return (this.providersByExtensionInterface.get(extensionInterface) ?? EMPTY) as T[];
  }

  /**
   * This can be used to get all extension with a specific interface.
   * It also returns the key of the extension which can be used together with {@link getProviderByKey} to fetch additional interface implementations.
   *
   * @example
   * registry.getKeyedProviders(DemoExtensionInterface).forEach(({key, provider}) => {
   *   const otherExtension = registry.getProviderByKey(key, SomeOtherDemoExtension)
   *   // ... do something with it
   * })
   */
  public getKeyedProviders<T extends ExtensionInterface>(extensionInterface: AbstractConstructor<T>): readonly KeyedExtension<T>[] {
    return (this.keyedProvidersByExtensionInterface.get(extensionInterface) ?? EMPTY) as KeyedExtension<T>[];
  }

  /**
   * This can be used to get further extension implementations.
   * It can only be used with keys retrieved by {@link getKeyedProviders}.
   *
   * @example
   * registry.getKeyedProviders(DemoExtensionInterface).forEach(({key, provider}) => {
   *   const otherExtension = registry.getProviderByKey(key, SomeOtherDemoExtension)
   *   // ... do something with it
   * })
   */
  public getProviderByKey<T extends ExtensionInterface>(
    key: ExtensionKey | undefined,
    extensionInterface: AbstractConstructor<T>,
  ): T | undefined {
    if (key === undefined) {
      return undefined;
    }
    return this.providerByKeyAndExtensionInterface.get(key)?.get(extensionInterface) as T | undefined;
  }
}
